import React, { useCallback, useEffect, useState } from "react";
import { Table, Input, Select, Button, Space } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  getAuditTrail,
  AuditAction,
} from "../../../services/features/audit.service";
import { httpService } from "../../../services/httpService.service";
import { formatDateAndTime } from "../../../services/helpFunctions/formatDateAndTime";
import Preview from "../planning/VisitBoard/TableView/modals/Preview";
import "./AuditTrail.css";

// Change values sometimes hold a raw entity GUID (e.g. a user, facility or IP
// reference). We resolve those to a human label (name / email) and never show a
// raw id — see `idMap` + `formatValue` below.
const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// F-11 — Audit trail. Filterable, read-only change log (System Admin / PM).
// Filters: entity id, action, AI-generated. Shows an "AI" badge when the
// change was made by an AI action. Site-visit rows link to the full Preview.

// Semantic action → branded pill class (Created/Updated/Deleted).
const actionClass = { 1: "created", 2: "updated", 3: "deleted" };

// The audit entityType for a site visit (matched loosely — casing varies).
const isSiteVisitEntity = (t) => (t || "").toLowerCase().includes("sitevisit");

const AuditTrail = () => {
  const projectID = localStorage.getItem("currentProjectID");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [entityId, setEntityId] = useState("");
  const [action, setAction] = useState(undefined);
  const [isAiGenerated, setIsAiGenerated] = useState(undefined);
  const [previewId, setPreviewId] = useState(null); // site visit being previewed
  // GUID (lowercased) → human label, for resolving reference values in the
  // Change column. Built from users (name/email), facilities and IP plans.
  const [idMap, setIdMap] = useState({});

  useEffect(() => {
    // Users → full name, else email.
    httpService.get(
      `/api/User/getAllusers?pageNumber=1&pageSize=1000`,
      (res) => {
        const next = {};
        (res?.data || []).forEach((u) => {
          const name = [u?.userDetails?.firstName, u?.userDetails?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          const label = name || u?.email;
          if (u?.id && label) next[String(u.id).toLowerCase()] = label;
        });
        setIdMap((m) => ({ ...m, ...next }));
      },
      () => {}
    );
    // Facilities → name.
    httpService.get(
      `/api/Facility`,
      (res) => {
        const list = res?.data || res || [];
        const next = {};
        (Array.isArray(list) ? list : []).forEach((f) => {
          if (f?.id && f?.name) next[String(f.id).toLowerCase()] = f.name;
        });
        setIdMap((m) => ({ ...m, ...next }));
      },
      () => {}
    );
    // IP plans → implementing partner name (or code).
    if (projectID) {
      httpService.get(
        `/api/IPPlan/getAllIPPlans?ProjectId=${projectID}&pageSize=1000&pageNumber=1`,
        (res) => {
          const list = res?.data || [];
          const next = {};
          (Array.isArray(list) ? list : []).forEach((ip) => {
            const label =
              ip?.implementingPartner?.name || ip?.ipPlanCode || ip?.identifier;
            if (ip?.id && label) next[String(ip.id).toLowerCase()] = label;
          });
          setIdMap((m) => ({ ...m, ...next }));
        },
        () => {}
      );
    }
  }, [projectID]);

  // Render a change value: resolve GUIDs to a label; never show a raw id.
  const formatValue = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const s = String(v).trim();
    if (GUID_RE.test(s)) return idMap[s.toLowerCase()] || "—";
    return s;
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    getAuditTrail(
      {
        entityId: entityId || undefined,
        projectId: projectID || undefined,
        action,
        isAiGenerated,
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
      },
      (res) => {
        const list = res?.data || res || [];
        setRows(Array.isArray(list) ? list : []);
        if (res?.metaData) {
          setPagination((prev) => ({
            ...prev,
            current: res.metaData.pageNumber ?? prev.current,
            pageSize: res.metaData.pageSize ?? prev.pageSize,
            total: res.metaData.totalItemCount ?? prev.total,
          }));
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, entityId, action, isAiGenerated, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 whenever a filter changes.
  const resetPage = () =>
    setPagination((prev) => ({ ...prev, current: 1 }));

  const handleTableChange = (p) =>
    setPagination((prev) => ({ ...prev, current: p.current, pageSize: p.pageSize }));

  const columns = [
    {
      title: "When",
      dataIndex: "timestamp",
      render: (v) => (v ? formatDateAndTime(v) : "—"),
      width: 160,
    },
    {
      title: "User",
      dataIndex: "userFullName",
      render: (v, r) =>
        r.isAiGenerated ? (
          <Space size={6}>
            <span>{v || "System"}</span>
            <span className="auditAiBadge">AI</span>
          </Space>
        ) : (
          v || "—"
        ),
      width: 180,
    },
    {
      title: "Entity",
      dataIndex: "entityType",
      width: 130,
      render: (v, r) =>
        isSiteVisitEntity(v) && r.entityId ? (
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() => setPreviewId(r.entityId)}
          >
            {v}
          </Button>
        ) : (
          v || "—"
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 110,
      render: (v) => (
        <span className={`auditTag auditTag--${actionClass[v] || "created"}`}>
          {AuditAction[v] || v}
        </span>
      ),
    },
    { title: "Field", dataIndex: "fieldName", width: 160 },
    {
      title: "Change",
      key: "change",
      render: (_, r) => {
        const oldV = formatValue(r.oldValue);
        const newV = formatValue(r.newValue);
        return (
          <span className="auditChange">
            <span className="auditVal auditVal--old" title={oldV}>
              {oldV}
            </span>
            <span className="auditArrow">→</span>
            <span className="auditVal auditVal--new" title={newV}>
              {newV}
            </span>
          </span>
        );
      },
    },
  ];

  return (
    <div className="page-content" style={{ padding: 24 }}>
      <div className="auditToolbar">
        <Input
          allowClear
          size="large"
          placeholder="Filter by entity id"
          style={{ width: 240 }}
          value={entityId}
          onChange={(e) => {
            resetPage();
            setEntityId(e.target.value);
          }}
        />
        <Select
          allowClear
          size="large"
          placeholder="Action"
          style={{ width: 140 }}
          value={action}
          onChange={(v) => {
            resetPage();
            setAction(v);
          }}
          options={[
            { value: 1, label: "Created" },
            { value: 2, label: "Updated" },
            { value: 3, label: "Deleted" },
          ]}
        />
        <Select
          allowClear
          size="large"
          placeholder="Source"
          style={{ width: 150 }}
          value={isAiGenerated}
          onChange={(v) => {
            resetPage();
            setIsAiGenerated(v);
          }}
          options={[
            { value: true, label: "AI-generated" },
            { value: false, label: "Human" },
          ]}
        />
        <Button
          size="large"
          className="inviteButtonTPM"
          icon={<ReloadOutlined />}
          onClick={fetchData}
        >
          Refresh
        </Button>
      </div>

      <Table
        className="tableTPM"
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={rows}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
        }}
        scroll={{ x: 900 }}
      />

      <Preview
        visible={!!previewId}
        onClose={() => setPreviewId(null)}
        siteVisitCardId={previewId}
      />
    </div>
  );
};

export default AuditTrail;
